import { getI18nLang } from '@yandex-int/i18n';
import { getIsYangoRequest } from 'client/utils';

import { routes } from '../lib/routes';
import { MerchantInfo, RequestMerchantAccessBody } from '../model/billing';
import { Image } from '../model/image';
import { ImageResource, ResourcesStatus, SoundResource } from '../model/resources';
import { Settings } from '../model/settings';
import { Channel, DonationSettings, HomepageFields, Skill } from '../model/skill';
import { ThereminvoxSoundMeta } from '../model/thereminvox';
import { UserAgreement, UserAgreementDraft } from '../model/userAgreement';
import { EmailConfirmationStage, NewsResponse, PhraseCommonness, Snapshot } from '../types';
import { CloudFunction } from '../types/cloud';
import { ImagesListResult, SoundsListResult } from '../types/utils';

import * as devicesApi from './services/devices';
import * as devicesTestingApi from './services/devicesTesting';
import * as granetApi from './services/grammar';
import * as shareApi from './services/share';
import * as skillTestApi from './services/skillTest';
import { callApi, callPublicApi, translateOperations } from './utils';
export { ApiError } from './error';
export { granetApi };
export { skillTestApi };
export { devicesApi };
export { shareApi };
export { devicesTestingApi };

export * from './appStoreApi';

export function createSkill(channel: Channel) {
    return callApi<Skill>({
        method: 'POST',
        url: routes.app.api('skills'),
        data: {
            channel,
            language: getI18nLang(),
            isYangoConsole: getIsYangoRequest(),
        },
    });
}

export function getSkill(id: string) {
    return callApi<Skill>({ method: 'GET', url: routes.app.api(`skills/${id}`) });
}

export function updateDraft(id: string, data: Partial<Settings>, channel: Channel) {
    return callApi<Skill>({
        method: 'PATCH',
        url:
            channel === Channel.AliceSkill || channel === Channel.SmartHome
                ? routes.app.appStoreApi(`apps/${id}/draft/update`)
                : routes.app.api(`skills/${id}/draft`),
        data: {
            ...data,
            channel,
        },
    });
}

export function attachOauth(id: string, oauthAppId: string) {
    return callApi<Skill>({ method: 'POST', url: routes.app.api(`skills/${id}/oauthApp`), data: { oauthAppId } });
}

export function deattachOauth(id: string) {
    return callApi<Skill>({ method: 'DELETE', url: routes.app.api(`skills/${id}/oauthApp`) });
}

export function updateSkillFlags(id: string, data: Partial<Skill>) {
    return callApi<Skill>({ method: 'PATCH', url: routes.app.api(`skills/${id}/flags`), data });
}

export function updateMainpageSettings(id: string, data: HomepageFields) {
    return callApi<Skill>({ method: 'PATCH', url: routes.app.api(`skills/${id}/homepage`), data });
}

export function requestSkillReview(id: string) {
    return callApi({ method: 'POST', url: routes.app.api(`skills/${id}/candidate`) });
}

export function cancelSkillReview(id: string) {
    return callApi({ method: 'POST', url: routes.app.api(`skills/${id}/draft/actions/withdraw`) });
}

interface RequestSkillDeployBody {
    draft: Skill['draft'];
    published: boolean;
}

export function requestSkillDeploy(id: string) {
    return callApi<RequestSkillDeployBody>({ method: 'POST', url: routes.app.api(`skills/${id}/release`) });
}

export function uploadImage(skillId: string, file: File) {
    const form = new FormData();

    form.append('file', file);

    return callApi<Image>({ method: 'POST', url: routes.app.api(`skills/${skillId}/logo`), data: form });
}

interface GetHostsParams {
    query: string;
}

export function getHostsForSuggest(params: GetHostsParams) {
    const encodedQuery = encodeURIComponent(params.query);

    return callApi<string[]>({ method: 'GET', url: routes.app.api('hosts', `?query=${encodedQuery}`) });
}

export async function getSnapshot() {
    const response = await callApi<Snapshot>({ method: 'GET', url: routes.app.api('snapshot') });

    const operations = translateOperations(response.operations);

    return {
        ...response,
        operations,
    };
}

export function subscribe() {
    return callApi({ method: 'POST', url: routes.app.api('subscription') });
}

export function unsubscribe(token?: string | (string | null)[]) {
    return callApi({ method: 'DELETE', url: routes.app.api('subscription'), data: { token } });
}

export function newsSubscribe() {
    return callApi({ method: 'POST', url: routes.app.api('news-subscription') });
}

export function newsUnsubscribe(token?: string) {
    return callApi({ method: 'DELETE', url: routes.app.api('news-subscription'), data: { token } });
}

interface EmailConfirmationResponse {
    stage: EmailConfirmationStage;
    skillId: string;
}

export function confirmEmail(token: string | (string | null)[]) {
    return callApi<EmailConfirmationResponse>({ method: 'POST', url: routes.app.api('email'), data: { token } });
}

interface SendEmailParams {
    userId: string;
    skillId: string;
    email: string;
}

export function sendEmail(data: SendEmailParams) {
    return callApi({ method: 'POST', url: routes.app.api('email/send'), data });
}

export function validateModerationTestToken(skillId: string, token: string | (string | null)[]) {
    return callApi({
        method: 'POST',
        url: routes.app.api(`moderation/skills/${skillId}/validate-token`),
        data: { token },
    });
}

export function getPhraseCommonness(phrase: string) {
    const encodedPhrase = encodeURIComponent(phrase);

    return callApi<PhraseCommonness>({
        method: 'GET',
        url: routes.app.api('/phrase-commonness', `?phrase=${encodedPhrase}`),
    });
}

export function getSkillImages(skillId: string, page: number) {
    return callPublicApi<ImagesListResult>({
        method: 'GET',
        url: routes.app.api(`public/v1/skills/${skillId}/images?page=${page}`),
    }).then((x) => ({
        items: x.images,
        total: x.total,
        limit: x.limit,
    }));
}

export function deleteSkillImage(skillId: string, imageId: string) {
    return callPublicApi({ method: 'DELETE', url: routes.app.api(`public/v1/skills/${skillId}/images/${imageId}`) });
}

export function uploadSkillImage(skillId: string, file: File) {
    const form = new FormData();
    form.append('file', file);

    return callPublicApi<{ image: ImageResource }>({
        method: 'POST',
        url: routes.app.api(`public/v1/skills/${skillId}/images`),
        data: form,
    }).then((x) => x.image);
}

export function getSkillSounds(skillId: string, page: number) {
    return callPublicApi<SoundsListResult>({
        method: 'GET',
        url: routes.app.api(`public/v1/skills/${skillId}/sounds?page=${page}`),
    }).then((x) => ({
        items: x.sounds,
        total: x.total,
        limit: x.limit,
    }));
}

export function deleteSkillSound(skillId: string, soundId: string) {
    return callPublicApi({ method: 'DELETE', url: routes.app.api(`public/v1/skills/${skillId}/sounds/${soundId}`) });
}

export function uploadSkillSound(skillId: string, file: File) {
    const form = new FormData();
    form.append('file', file);

    return callPublicApi<{ sound: SoundResource }>({
        method: 'POST',
        url: routes.app.api(`public/v1/skills/${skillId}/sounds`),
        data: form,
    }).then((x) => x.sound);
}

export function uploadThereminvoxSound(skillId: string, file: File, onUploadProgress?: () => void) {
    const form = new FormData();
    form.append('file', file);

    return callApi<ThereminvoxSoundMeta>({
        method: 'POST',
        url: routes.app.api(`skills/${skillId}/soundset/sounds`),
        data: form,
        onUploadProgress,
    });
}

export function getSkillSound(skillId: string, soundId: string) {
    return callPublicApi<{ sound: SoundResource }>({
        method: 'GET',
        url: routes.app.api(`public/v1/skills/${skillId}/sounds/${soundId}`),
    }).then((x) => x.sound);
}

export function getResourcesStatus() {
    return callPublicApi<ResourcesStatus>({ method: 'GET', url: routes.app.api('public/v1/status') });
}

export function requestMerchantAccess(skillId: string, data: RequestMerchantAccessBody) {
    return callApi<MerchantInfo>({
        method: 'PUT',
        url: routes.app.api(`/billing/skills/${skillId}/request_merchant_access`),
        data,
    });
}

export function getPublicKey(skillId: string) {
    return callApi<string>({ method: 'GET', url: routes.app.api(`/billing/skills/${skillId}/public_key`) });
}

export function getMerchants(skillId: string) {
    return callApi<MerchantInfo[]>({ method: 'GET', url: routes.app.api(`/billing/skills/${skillId}/merchants`) });
}

export function registerBilling(skillId: string) {
    return callApi<'ok'>({ method: 'PUT', url: routes.app.api(`/billing/skills/${skillId}`) });
}

export function getLastNews() {
    return callApi<NewsResponse>({ method: 'GET', url: routes.app.api('blog/last-news') });
}

export function banNews(newsId: string) {
    return callApi({ method: 'POST', url: routes.app.api(`blog/ban-news/${newsId}`) });
}

export function getCloudFunctions() {
    return callApi<CloudFunction[]>({ method: 'GET', url: routes.app.api('/cloud-functions') });
}

export function validateCloudFunction(functionId: string) {
    return callApi<'ok'>({ method: 'POST', url: routes.app.api(`/cloud-functions/${functionId}/validate`) });
}

export function updateDonationSettings(skillId: string, settings: DonationSettings) {
    return callApi<'ok'>({
        method: 'PATCH',
        url: routes.app.api(`/skills/${skillId}/donation/settings`),
        data: settings,
    });
}

export function deleteDonationSettings(skillId: string) {
    return callApi<'ok'>({
        method: 'DELETE',
        url: routes.app.api(`/skills/${skillId}/donation/settings`),
    });
}

export const createUserAgreement = (data: Pick<UserAgreementDraft | UserAgreement, 'name' | 'url'>, skillId: string) =>
    callApi<UserAgreement>({
        data,
        method: 'POST',
        url: routes.app.api(`/skills/${skillId}/user-agreements/drafts`),
    });

export const updateUserAgreement = (
    data: Pick<UserAgreementDraft | UserAgreement, 'name' | 'url'> & Record<'id', unknown>,
    skillId: string,
) =>
    callApi({
        data,
        method: 'PATCH',
        url: routes.app.api(`/skills/${skillId}/user-agreements/${data.id}/draft`),
    });

export const deleteUserAgreement = (data: UserAgreement, skillId: string) =>
    callApi({
        method: 'DELETE',
        url: routes.app.api(`/skills/${skillId}/user-agreements/${data.id}/draft`),
    });

export const getAgreements = (skillId: string, isDraft: boolean) =>
    callApi<UserAgreement[]>({
        method: 'GET',
        url: routes.app.api(`/skills/${skillId}/user-agreements${isDraft ? '/drafts' : ''}`),
    });

export const reorderAgreements = (skillId: string, newOrder: Record<string, number>) =>
    callApi({
        method: 'POST',
        url: routes.app.api(`/skills/${skillId}/user-agreements/drafts/reorder`),
        data: newOrder,
    });
